import React, { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { createErrorCopyAction } from "./ui/Toaster";
import { usePendingOrders } from "../hooks/usePendingOrders";
import { PendingOrder } from "../sdk/types/data";
import { OrderType } from "../sdk/types/api";
import { useMarkets } from "../hooks/useMarkets";
import { ExchangeClient, SubscriptionClient } from "../sdk";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  WarningMessage,
  LayoutHeader,
  TableHeaderCell,
  TableHeaderCellStatic,
  CancelButton,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_PENDING_ORDERS,
  DEFAULT_REFRESH_INTERVAL,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  ORDER_SORT_FIELD_SYMBOL,
  ORDER_SORT_FIELD_SIDE,
  ORDER_SORT_FIELD_QUANTITY,
  ORDER_SORT_FIELD_PRICE,
  ORDER_SORT_FIELD_FILLED_QUANTITY,
  ORDER_SORT_FIELD_STATUS,
  ORDER_VIEW_MODE_PENDING,
  ORDER_VIEW_MODE_HISTORY,
  ORDER_PENDING_STATUSES,
  ORDER_STATUS_COLOR_PENDING,
  ORDER_STATUS_COLOR_OPEN,
  ORDER_STATUS_COLOR_FILLED,
  ORDER_STATUS_COLOR_CANCELED,
  ORDER_STATUS_COLOR_UNKNOWN,
  ORDER_SIDE_COLOR_BUY_LONG,
  ORDER_SIDE_COLOR_SELL_SHORT,
  ORDER_GRID_TEMPLATE_COLUMNS,
  ORDER_LOADING_MESSAGE,
  ORDER_EMPTY_MESSAGE_PENDING,
  ORDER_EMPTY_MESSAGE_HISTORY,
  ORDER_TITLE_PENDING,
  ORDER_TITLE_HISTORY,
  ORDER_WARNING_WALLET_REQUIRED,
  ORDER_TAB_LABEL_PENDING,
  ORDER_TAB_LABEL_HISTORY,
  ORDER_TYPE_FALLBACK,
  ORDER_STATUS_UNKNOWN,
  ORDER_SIDE_LONG,
  OrderSortField,
  OrderSortDirection,
  OrderViewMode,
} from "./smartlayout/constants";
import "./smartlayout/layout.css";

interface PendingOrdersProps {
  baseURL: string;
  subscriptionClient: SubscriptionClient;
  authToken?: string;
  walletAddress: string;
  mnemonic?: string; // Required for canceling orders
  height: string;
  refreshInterval?: number;
}

function PendingOrders({
  baseURL = DEFAULT_BASE_URL,
  subscriptionClient,
  authToken,
  walletAddress,
  mnemonic,
  height = DEFAULT_HEIGHT_PENDING_ORDERS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
}: PendingOrdersProps): JSX.Element {
  const { orders, loading, error, isConnected, refresh } = usePendingOrders({
    subscriptionClient,
    baseURL,
    authToken,
    walletAddress,
    refreshInterval,
  });

  const exchangeClientRef = useRef<ExchangeClient | null>(null);
  // Ref to store the loading toast ID so we can dismiss it later
  const loadingToastIdRef = useRef<number>(0);
  const [cancelingOrderId, setCancelingOrderId] = useState<string | null>(null);

  // Get markets for symbol mapping (optional - will fallback to hardcoded if not available)
  const { getSymbolByMarketId } = useMarkets({
    baseURL,
    sessionToken: authToken,
    autoFetchMarkets: false, // Don't auto-fetch, just use if already loaded
  });

  const [viewMode, setViewMode] = useState<OrderViewMode>(
    ORDER_VIEW_MODE_PENDING,
  );
  const [sortField, setSortField] = useState<OrderSortField | null>(null);
  const [sortDirection, setSortDirection] =
    useState<OrderSortDirection>(SORT_DIRECTION_DESC);

  // Filter orders based on view mode
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // Status is now required, but we normalize it for comparison
      const status = order.status.toUpperCase();
      const normalizedStatus = status.startsWith("ORDER_STATUS_")
        ? status
        : `ORDER_STATUS_${status}`;

      if (viewMode === ORDER_VIEW_MODE_PENDING) {
        return ORDER_PENDING_STATUSES.some(
          (pendingStatus) =>
            status === pendingStatus.toUpperCase() ||
            normalizedStatus === pendingStatus.toUpperCase(),
        );
      } else {
        // History view: all orders that are NOT pending
        return !ORDER_PENDING_STATUSES.some(
          (pendingStatus) =>
            status === pendingStatus.toUpperCase() ||
            normalizedStatus === pendingStatus.toUpperCase(),
        );
      }
    });
  }, [orders, viewMode]);

  // Sort orders
  const sortedOrders = useMemo(() => {
    if (!sortField) return filteredOrders;

    return [...filteredOrders].sort((a: PendingOrder, b: PendingOrder) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case ORDER_SORT_FIELD_SYMBOL:
          aValue = getSymbolByMarketId(a.marketId) || `m-${a.marketId}`;
          bValue = getSymbolByMarketId(b.marketId) || `m-${b.marketId}`;
          break;
        case ORDER_SORT_FIELD_SIDE:
          aValue = a.side;
          bValue = b.side;
          break;
        case ORDER_SORT_FIELD_QUANTITY:
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case ORDER_SORT_FIELD_PRICE:
          aValue = a.price;
          bValue = b.price;
          break;
        case ORDER_SORT_FIELD_FILLED_QUANTITY:
          aValue = a.filledQuantity || 0;
          bValue = b.filledQuantity || 0;
          break;
        case ORDER_SORT_FIELD_STATUS:
          aValue = a.status;
          bValue = b.status;
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === SORT_DIRECTION_ASC
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      // Safety check: if either value is NaN, treat as equal (0)
      if (isNaN(numA) || isNaN(numB)) {
        return 0;
      }

      return sortDirection === SORT_DIRECTION_ASC ? numA - numB : numB - numA;
    });
  }, [filteredOrders, sortField, sortDirection]);

  const handleSort = (field: OrderSortField) => {
    if (sortField === field) {
      setSortDirection(
        sortDirection === SORT_DIRECTION_ASC
          ? SORT_DIRECTION_DESC
          : SORT_DIRECTION_ASC,
      );
    } else {
      setSortField(field);
      setSortDirection(SORT_DIRECTION_DESC);
    }
  };

  // Helper: Get side display - returns side as-is since it's already normalized to "BUY" | "SELL" | "LONG" | "SHORT"
  const getOrderSide = (order: PendingOrder): string => order.side;

  // Helper: Get short status with full name for tooltip
  const getShortStatus = (
    status: string | undefined,
  ): { short: string; full: string; color: string } => {
    const normalizedStatus = (status || "").toUpperCase();

    // Status map based on OrderStatus type from API
    // Status is already normalized by transformRawOrderToPendingOrder (ORDER_STATUS_ prefix removed)
    // Only includes the 5 valid normalized status values: OPEN, FILLED, CANCELLED, PARTIAL, PENDING
    const statusMap: Record<
      string,
      { short: string; full: string; color: string }
    > = {
      PENDING: {
        short: "P",
        full: "Pending",
        color: ORDER_STATUS_COLOR_PENDING,
      },
      OPEN: {
        short: "O",
        full: "Open",
        color: ORDER_STATUS_COLOR_OPEN,
      },
      FILLED: {
        short: "F",
        full: "Filled",
        color: ORDER_STATUS_COLOR_FILLED,
      },
      CANCELLED: {
        short: "C",
        full: "Canceled",
        color: ORDER_STATUS_COLOR_CANCELED,
      },
      PARTIAL: {
        short: "PF",
        full: "Partially Filled",
        color: ORDER_STATUS_COLOR_PENDING,
      },
    };

    // Try exact match (status is already normalized)
    if (statusMap[normalizedStatus]) {
      return statusMap[normalizedStatus];
    }

    // Default fallback
    return {
      short: "?",
      full: status || ORDER_STATUS_UNKNOWN,
      color: ORDER_STATUS_COLOR_UNKNOWN,
    };
  };

  // Helper: Get side color
  const getSideColor = (side: string): string => {
    // side is "BUY" | "SELL" | "LONG" | "SHORT" (uppercase)
    return side === ORDER_SIDE_LONG || side === "BUY"
      ? ORDER_SIDE_COLOR_BUY_LONG
      : ORDER_SIDE_COLOR_SELL_SHORT;
  };

  // Helper: Format order type for display (remove ORDER_TYPE_ prefix)
  const getOrderType = (type: OrderType): string => {
    if (!type || typeof type !== "string") {
      return ORDER_TYPE_FALLBACK;
    }
    // Simply remove ORDER_TYPE_ prefix for display
    return type.replace("ORDER_TYPE_", "");
  };

  const handleCancelOrder = async (order: PendingOrder) => {
    // Get order ID and market ID first for better error messages
    // Both orderId and id are required fields, but we prefer orderId
    const orderId = order.orderId;
    const marketId = order.marketId;

    // Validate order data
    if (!orderId) {
      const errorMsg = "Cannot cancel order without a valid order ID.";
      toast.error("Order ID is missing", {
        description: errorMsg,
        action: createErrorCopyAction(errorMsg),
      });
      return;
    }

    if (!marketId) {
      const errorMsg = "Cannot cancel order without a valid market ID.";
      toast.error("Market ID is missing", {
        description: errorMsg,
        action: createErrorCopyAction(errorMsg),
      });
      return;
    }

    // Check if mnemonic is available
    if (!mnemonic) {
      const errorMsg = `Please provide the 'mnemonic' prop to the PendingOrders component. Order ID: ${orderId}`;
      toast.error("Mnemonic required", {
        description: errorMsg,
        duration: 5000,
        action: createErrorCopyAction(errorMsg),
      });
      return;
    }

    if (typeof mnemonic !== "string" || mnemonic.trim() === "") {
      const errorMsg = `The mnemonic prop is empty or invalid. Please provide a valid mnemonic. Order ID: ${orderId}`;
      toast.error("Invalid mnemonic", {
        description: errorMsg,
        duration: 5000,
        action: createErrorCopyAction(errorMsg),
      });
      return;
    }

    // Prevent multiple simultaneous cancellations
    if (cancelingOrderId) {
      toast.info("Cancellation in progress", {
        description: "Please wait for the current cancellation to complete.",
      });
      return; // Already canceling an order
    }

    setCancelingOrderId(orderId);

    // Show loading toast
    const toastId = toast.loading("Canceling order...", {
      description: `Canceling order ${orderId}`,
    });
    loadingToastIdRef.current = typeof toastId === "number" ? toastId : 0;

    try {
      // Initialize ExchangeClient if not already initialized
      if (!exchangeClientRef.current) {
        exchangeClientRef.current = new ExchangeClient({
          baseURL,
          mnemonic: mnemonic.trim(),
        });
        await exchangeClientRef.current.initialize();
      }

      // Cancel the order
      const response = await exchangeClientRef.current.cancelOrder({
        orderId,
        marketId,
      });

      // Check if cancellation was successful
      // The API may return different response structures, so we check for success indicators
      const isSuccess =
        response.success === true ||
        response.result.success === true ||
        (response && !response.error && !response.message);

      // Dismiss loading toast
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }
      const tx = response.txhash
      if (isSuccess) {
        toast.success("Order canceled successfully", {
          description: `Order ${orderId} has been canceled. TX: ${tx}`,
        });
        // Refresh orders to update the UI
        if (refresh) {
          await refresh();
        }
      } else {
        const errorMsg =
          response?.error ||
          response?.message ||
          response?.response?.error ||
          "Failed to cancel order";
        throw new Error(errorMsg);
      }
    } catch (error) {
      // Dismiss loading toast
      if (loadingToastIdRef.current !== 0) {
        toast.dismiss(loadingToastIdRef.current);
        loadingToastIdRef.current = 0;
      }
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      const fullErrorMessage = `${errorMessage}. Order ID: ${orderId}`;
      toast.error("Failed to cancel order", {
        description: fullErrorMessage,
        duration: 6000,
        action: createErrorCopyAction(fullErrorMessage),
      });
    } finally {
      setCancelingOrderId(null);
    }
  };

  // Show appropriate message when wallet address is not available
  if (!walletAddress || walletAddress.trim() === "") {
    return <WarningMessage message={ORDER_WARNING_WALLET_REQUIRED} />;
  }

  if (loading) {
    return <LoadingMessage message={ORDER_LOADING_MESSAGE} />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* Header */}
      <LayoutHeader
        title={
          viewMode === ORDER_VIEW_MODE_PENDING
            ? ORDER_TITLE_PENDING
            : ORDER_TITLE_HISTORY
        }
      >
        <div className="btn-tab">
          <span style={{ fontSize: "0.7rem" }}>▼</span>
          <span>Current Symbol</span>
        </div>
        <div
          onClick={() =>
            setViewMode(
              viewMode === ORDER_VIEW_MODE_PENDING
                ? ORDER_VIEW_MODE_HISTORY
                : ORDER_VIEW_MODE_PENDING,
            )
          }
          className={`btn-tab ${viewMode === ORDER_VIEW_MODE_HISTORY ? "active" : ""}`}
        >
          <img src="/transfer-net.svg" />
          <span>
            {viewMode === ORDER_VIEW_MODE_PENDING
              ? ORDER_TAB_LABEL_HISTORY
              : ORDER_TAB_LABEL_PENDING}
          </span>
        </div>
        {!isConnected && (
          <div
            onClick={async () => {
              if (refresh) {
                await refresh();
              }
            }}
            className="btn-tab"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            title="WebSocket disconnected - Click to refresh orders"
          >
            <img
              src="/caution-icy.svg"
              alt="Warning"
              style={{ width: "1rem", height: "1rem" }}
            />
            <span>Refresh</span>
          </div>
        )}
      </LayoutHeader>

      {/* Table */}
      <div className="table-container shrinkable">
        {/* Table Header */}
        <div
          className="table-header"
          style={{
            gridTemplateColumns: ORDER_GRID_TEMPLATE_COLUMNS,
          }}
        >
          <TableHeaderCell
            field={ORDER_SORT_FIELD_SYMBOL}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Symbol
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_SIDE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Side
          </TableHeaderCell>
          <TableHeaderCellStatic center>Type</TableHeaderCellStatic>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_QUANTITY}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Quantity
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_PRICE}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Price
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_FILLED_QUANTITY}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Filled
          </TableHeaderCell>
          <TableHeaderCell
            field={ORDER_SORT_FIELD_STATUS}
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Status
          </TableHeaderCell>
          <TableHeaderCellStatic center>Actions</TableHeaderCellStatic>
        </div>

        {/* Table Body */}
        {sortedOrders.length === 0 ? (
          <EmptyMessage
            message={
              viewMode === ORDER_VIEW_MODE_PENDING
                ? ORDER_EMPTY_MESSAGE_PENDING
                : ORDER_EMPTY_MESSAGE_HISTORY
            }
          />
        ) : (
          sortedOrders.map((order: PendingOrder, index: number) => {
            const side = getOrderSide(order);
            const sideColor = getSideColor(side);
            const {
              short: shortStatus,
              full: fullStatus,
              color: statusColor,
            } = getShortStatus(order.status);
            // Use getSymbolByMarketId to look up symbol by marketId
            const marketSymbol =
              getSymbolByMarketId(order.marketId) || `m-${order.marketId}`;

            return (
              <div
                key={`order-${order.orderId}`}
                className={`table-row ${index % 2 === 0 ? "even" : ""}`}
                style={{
                  gridTemplateColumns: ORDER_GRID_TEMPLATE_COLUMNS,
                }}
              >
                {/* Symbol */}
                <div
                  className="table-cell primary"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span>{marketSymbol}</span>
                </div>

                {/* Side */}
                <div
                  className="table-cell center"
                  style={{ color: sideColor, fontWeight: 500 }}
                >
                  {side}
                </div>

                {/* Type */}
                <div className="table-cell center">
                  {getOrderType(order.type)}
                </div>

                {/* Quantity */}
                <div className="table-cell right monospace">
                  {order.quantity}
                </div>

                {/* Price */}
                <div className="table-cell right monospace">{order.price}</div>

                {/* Filled */}
                <div className="table-cell right monospace">
                  {order.filledQuantity} / {order.quantity}
                </div>

                {/* Status */}
                <div
                  className="table-cell center"
                  style={{
                    color: statusColor,
                    fontWeight: 600,
                    cursor: "help",
                    fontSize: "0.9rem",
                  }}
                  title={fullStatus}
                >
                  {shortStatus}
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {viewMode === ORDER_VIEW_MODE_PENDING ? (
                    <>
                      {(!mnemonic || mnemonic.trim() === "") && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "#ffc107",
                            cursor: "help",
                          }}
                          title="Mnemonic required to cancel orders"
                        >
                          ⚠️
                        </span>
                      )}
                      <CancelButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const isDisabled =
                            !mnemonic ||
                            mnemonic.trim() === "" ||
                            cancelingOrderId === order.orderId ||
                            !!cancelingOrderId;
                          if (!isDisabled) {
                            handleCancelOrder(order);
                          }
                        }}
                        disabled={
                          !mnemonic ||
                          mnemonic.trim() === "" ||
                          cancelingOrderId === order.orderId ||
                          !!cancelingOrderId
                        }
                      />
                    </>
                  ) : (
                    <span className="color-text-dim">—</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Memoize component to prevent re-renders when props haven't changed
export default React.memo(PendingOrders);
